import type { ReactElement, ReactNode, Ref } from 'react'

import { Icon } from '../icon/icon.tsx'
import type { IconName } from '../icon/icon-paths.ts'

export type PillVariant = 'primary' | 'secondary' | 'ghost' | 'toggle'

/** Appended to the accessible name of any link that leaves the document. */
export const NEW_TAB_HINT = '(abre em nova aba)'

interface PillContentProps {
  readonly icon?: IconName
  readonly iconAfter?: IconName
  readonly children: ReactNode
}

function PillContent({ icon, iconAfter, children }: PillContentProps): ReactElement {
  return (
    <>
      {icon === undefined ? null : <Icon name={icon} size={17} decorative />}
      <span className="pill__label">{children}</span>
      {iconAfter === undefined ? null : <Icon name={iconAfter} size={17} decorative />}
    </>
  )
}

/**
 * Marks a pill whose LEADING icon can stand in for its label on a phone.
 *
 * Below `$small-screen-ceiling` the stylesheet clips `.pill__label` out of sight
 * and the control becomes its icon alone — which only reads as a control at all
 * when the icon identifies the action. A leading icon does (a book, a chevron, a
 * share glyph); `iconAfter` is trailing chrome, and the hero's "Ler os poemas"
 * reduced to a bare arrow would be a button with nothing in it. So the modifier
 * follows `icon` and never `iconAfter`.
 *
 * The label is CLIPPED, NOT REMOVED. `display: none` would take the accessible
 * name with it and leave an unnamed button — § 4.1.2 — so the text stays in the
 * accessibility tree at every width and only its pixels go away.
 */
function pillClassName(
  variant: PillVariant,
  icon: IconName | undefined,
  extras: readonly string[],
): string {
  return [
    'pill',
    `pill--${variant}`,
    icon === undefined ? '' : 'pill--has-icon',
    ...extras,
  ]
    .filter((token) => token !== '')
    .join(' ')
}

export interface PillButtonProps extends PillContentProps {
  readonly variant?: PillVariant
  readonly onClick: () => void
  /**
   * Only meaningful on the `toggle` variant, where it becomes `aria-pressed`.
   *
   * The design drove this imperatively — `syncModos()` reached into the DOM and
   * set `style.background` on every button after each click. The pressed state is
   * a render input, so it renders.
   */
  readonly pressed?: boolean
  readonly label?: string
  /**
   * Disclosure state, for a button that shows and hides something else.
   *
   * SEPARATE FROM `pressed`, which is `aria-pressed` and means "this option is
   * selected". A disclosure is not a selection: `aria-expanded` tells a screen
   * reader that activating the button will reveal or hide the region named by
   * `controls`, which is what the mobile navigation toggle actually does.
   */
  readonly expanded?: boolean
  /** Id of the region `expanded` refers to. Meaningless without it. */
  readonly controls?: string
  /**
   * Escapes the component so a caller can return focus here.
   *
   * A disclosure that collapses while focus is inside it strands that focus on
   * `<body>` (§ 2.4.3). The owner of the open/closed state is the only code that
   * knows a collapse is about to happen, so it needs a handle on the button.
   */
  readonly buttonRef?: Ref<HTMLButtonElement>
  /** Extra class for callers that must position the control. */
  readonly className?: string
}

export function PillButton({
  variant = 'primary',
  onClick,
  pressed,
  label,
  expanded,
  controls,
  buttonRef,
  className,
  icon,
  iconAfter,
  children,
}: PillButtonProps): ReactElement {
  return (
    <button
      type="button"
      ref={buttonRef}
      className={pillClassName(variant, icon, [
        pressed === true ? 'pill--is-pressed' : '',
        className ?? '',
      ])}
      onClick={onClick}
      aria-pressed={pressed}
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={controls}
    >
      <PillContent icon={icon} iconAfter={iconAfter}>
        {children}
      </PillContent>
    </button>
  )
}

export interface PillLinkProps extends PillContentProps {
  readonly variant?: PillVariant
  readonly href: string
  /** Opens in a new tab, with the rel hardening and the audible hint that implies. */
  readonly external?: boolean
  readonly rel?: 'prev' | 'next'
  /**
   * Visually-hidden text prepended to the accessible name. Use when the visible
   * label is truncated or too terse to stand alone out of context — § 2.4.4.
   */
  readonly accessibleName?: string
}

/**
 * A link, not a polymorphic button.
 *
 * Two components rather than one `as` prop: a button does something, a link goes
 * somewhere, and their keyboard and screen-reader contracts differ. Collapsing
 * them into one component would hide that difference behind a prop.
 */
export function PillLink({
  variant = 'secondary',
  href,
  external = false,
  rel,
  accessibleName,
  icon,
  iconAfter,
  children,
}: PillLinkProps): ReactElement {
  // `noopener` severs the `window.opener` reference; `noreferrer` also withholds
  // the Referer header. The design set both — the only thing it omitted was
  // telling the reader that the link leaves the page.
  const relParts = [external ? 'noopener noreferrer' : undefined, rel].filter(
    (part): part is string => part !== undefined,
  )

  return (
    <a
      className={pillClassName(variant, icon, [])}
      href={href}
      target={external ? '_blank' : undefined}
      rel={relParts.length === 0 ? undefined : relParts.join(' ')}
    >
      {accessibleName === undefined ? null : (
        <span className="visually-hidden">{accessibleName}</span>
      )}
      <PillContent icon={icon} iconAfter={iconAfter}>
        {children}
      </PillContent>
      {external ? <span className="visually-hidden"> {NEW_TAB_HINT}</span> : null}
    </a>
  )
}
